import * as THREE from 'three';
import { patchMaterial } from '../render/materials';
import { COMMON_UNIFORMS, TERRAIN_HEIGHT_FNS } from '../shaders/common';
import type { Environment } from './Environment';
import type { Terrain } from './Terrain';
import { TERRAIN_CELL, TERRAIN_HALF, TERRAIN_RES } from './Terrain';
import { clamp, damp, lerp, mulberry32 } from './noise';

const SEED_COUNT = 1400;
const SEED_BOX = new THREE.Vector3(44, 9, 44);

/** Drifting susuki seed fluff that glows when backlit. */
export class Seeds {
  readonly points: THREE.Points;
  private readonly uniforms: Record<string, THREE.IUniform>;
  private readonly drift = new THREE.Vector3();

  constructor(env: Environment, terrain: Terrain, count = SEED_COUNT) {
    const rng = mulberry32(77);
    const positions = new Float32Array(count * 3);
    const params = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      positions.set([rng() * SEED_BOX.x, rng(), rng() * SEED_BOX.z], i * 3);
      params.set([rng(), 0.5 + rng(), rng(), rng()], i * 4);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('params', new THREE.BufferAttribute(params, 4));
    this.uniforms = {
      ...env.uniforms,
      uHeightTex: { value: terrain.heightTexture },
      uHeightInfo: { value: new THREE.Vector3(TERRAIN_HALF, TERRAIN_CELL, TERRAIN_RES) },
      uDrift: { value: this.drift },
      uBox: { value: SEED_BOX },
      uPixelScale: { value: 400 },
    };
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        ${COMMON_UNIFORMS}
        ${TERRAIN_HEIGHT_FNS}
        uniform vec3 uDrift;
        uniform vec3 uBox;
        uniform float uPixelScale;
        attribute vec4 params;
        varying float vGlow;
        varying float vFade;
        void main() {
          vec3 base = vec3(position.x, 0.0, position.z);
          vec3 p = base + vec3(uDrift.x, 0.0, uDrift.z) * params.y;
          p.x += sin(uTime * (0.6 + params.z) + params.w * 30.0) * 0.6;
          p.z += cos(uTime * (0.5 + params.w) + params.z * 30.0) * 0.6;
          vec3 cam = cameraPosition;
          p.xz = mod(p.xz - cam.xz + uBox.xz * 0.5, uBox.xz) - uBox.xz * 0.5 + cam.xz;
          float ground = terrainHeight(p.xz);
          p.y = ground + 1.3 + position.y * uBox.y * (0.35 + 0.65 * params.x) + sin(uTime * 0.8 + params.w * 20.0) * 0.35;
          vec3 toP = p - cam;
          float dist = length(toP);
          vec3 viewDir = toP / max(dist, 1e-3);
          float back = pow(max(dot(viewDir, uSunDir), 0.0), 5.0);
          vGlow = 0.22 + back * 2.2;
          vec2 edge = abs(p.xz - cam.xz) / (uBox.xz * 0.5);
          vFade = (1.0 - smoothstep(0.7, 1.0, max(edge.x, edge.y))) * smoothstep(0.6, 2.0, dist);
          vec4 mv = viewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = clamp(uPixelScale * (0.03 + params.x * 0.022) / -mv.z, 1.5, 14.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uSunColor;
        varying float vGlow;
        varying float vFade;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          float a = smoothstep(0.5, 0.0, d);
          a *= a;
          gl_FragColor = vec4(uSunColor * vec3(1.0, 0.86, 0.62) * 0.2 * vGlow * a * vFade, 1.0);
        }
      `,
    });
    this.points = new THREE.Points(geometry, material);
    this.points.frustumCulled = false;
    this.points.name = 'seeds';
  }

  update(dt: number, windDir: THREE.Vector2, strength: number, gust: number, viewportHeight: number): void {
    const speed = 1.2 + strength * 2.8 + gust * 2.5;
    this.drift.x += windDir.x * speed * dt;
    this.drift.z += windDir.y * speed * dt;
    this.uniforms.uPixelScale.value = viewportHeight;
  }
}

const GUIDE_COUNT = 900;

/** Stream of glowing fluff that flows toward the objective while the guiding wind blows. */
export class GuideStream {
  readonly points: THREE.Points;
  private readonly uniforms: Record<string, THREE.IUniform>;

  constructor(env: Environment, terrain: Terrain) {
    const rng = mulberry32(12);
    const positions = new Float32Array(GUIDE_COUNT * 3);
    for (let i = 0; i < GUIDE_COUNT; i++) positions.set([rng(), rng(), rng()], i * 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.uniforms = {
      ...env.uniforms,
      uHeightTex: { value: terrain.heightTexture },
      uHeightInfo: { value: new THREE.Vector3(TERRAIN_HALF, TERRAIN_CELL, TERRAIN_RES) },
      uOrigin: { value: new THREE.Vector3() },
      uDirection: { value: new THREE.Vector2(1, 0) },
      uAmount: { value: 0 },
      uLength: { value: 40 },
      uPixelScale: { value: 400 },
    };
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        ${COMMON_UNIFORMS}
        ${TERRAIN_HEIGHT_FNS}
        uniform vec3 uOrigin;
        uniform vec2 uDirection;
        uniform float uAmount;
        uniform float uLength;
        uniform float uPixelScale;
        varying float vAlpha;
        void main() {
          float t = fract(position.x + uTime * (0.1 + position.y * 0.05));
          vec2 side = vec2(-uDirection.y, uDirection.x);
          float spread = (position.z - 0.5) * (7.0 + t * 5.0);
          vec2 xz = uOrigin.xz + uDirection * (-7.0 + t * (uLength + 7.0)) + side * (spread + sin(t * 12.0 + position.y * 40.0) * 0.9);
          float ground = terrainHeight(xz);
          vec3 p = vec3(xz.x, ground + 1.45 + position.y * 1.9 + sin(t * 9.0 + position.z * 20.0) * 0.35, xz.y);
          vAlpha = uAmount * sin(t * 3.14159) * smoothstep(0.0, 0.08, t);
          vec4 mv = viewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = clamp(uPixelScale * 0.14 / -mv.z, 3.0, 28.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uSunColor;
        varying float vAlpha;
        void main() {
          float d = length(gl_PointCoord - 0.5);
          float a = smoothstep(0.5, 0.0, d);
          float core = smoothstep(0.18, 0.0, d);
          gl_FragColor = vec4(uSunColor * vec3(1.0, 0.82, 0.52) * (0.32 * a * a + 0.9 * core) * vAlpha, 1.0);
        }
      `,
    });
    this.points = new THREE.Points(geometry, material);
    this.points.frustumCulled = false;
    this.points.name = 'guide-stream';
  }

  update(origin: THREE.Vector3, direction: THREE.Vector2, amount: number, length: number, viewportHeight: number): void {
    this.uniforms.uOrigin.value.copy(origin);
    this.uniforms.uDirection.value.copy(direction).normalize();
    this.uniforms.uAmount.value = amount;
    this.uniforms.uLength.value = length;
    this.uniforms.uPixelScale.value = viewportHeight;
    this.points.visible = amount > 0.01;
  }
}

type Fly = { pos: THREE.Vector3; target: THREE.Vector3; vel: THREE.Vector3; hover: number; heading: number; phase: number };

const DRAGONFLY_COUNT = 9;

/** Red dragonflies (akatombo) that hover and dart around the player. */
export class Dragonflies {
  readonly group = new THREE.Group();
  private readonly bodies: THREE.InstancedMesh;
  private readonly wings: THREE.InstancedMesh;
  private readonly flies: Fly[] = [];
  private readonly rng = mulberry32(5);
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly s = new THREE.Vector3(1, 1, 1);
  private readonly e = new THREE.Euler();
  private time = 0;

  constructor(env: Environment, private readonly terrain: Terrain) {
    const bodyGeo = new THREE.CapsuleGeometry(0.0045, 0.055, 3, 6);
    bodyGeo.rotateX(Math.PI / 2);
    const bodyMat = patchMaterial(new THREE.MeshStandardMaterial({ color: 0x9c2410, roughness: 0.45, emissive: 0x2a0500 }), env.uniforms, { rim: 0.8 });
    this.bodies = new THREE.InstancedMesh(bodyGeo, bodyMat, DRAGONFLY_COUNT);
    const wingGeo = new THREE.PlaneGeometry(0.05, 0.011);
    wingGeo.translate(0.025, 0, 0);
    wingGeo.rotateX(-Math.PI / 2);
    const wingMat = new THREE.MeshBasicMaterial({ color: 0xfff2d8, transparent: true, opacity: 0.28, side: THREE.DoubleSide, depthWrite: false });
    this.wings = new THREE.InstancedMesh(wingGeo, wingMat, DRAGONFLY_COUNT * 4);
    this.bodies.frustumCulled = false;
    this.wings.frustumCulled = false;
    this.group.add(this.bodies, this.wings);
    for (let i = 0; i < DRAGONFLY_COUNT; i++) {
      this.flies.push({ pos: new THREE.Vector3(), target: new THREE.Vector3(), vel: new THREE.Vector3(), hover: this.rng() * 2, heading: 0, phase: this.rng() * 10 });
    }
  }

  reset(center: THREE.Vector3): void {
    for (const fly of this.flies) {
      this.pickTarget(fly, center);
      fly.pos.copy(fly.target);
    }
  }

  private pickTarget(fly: Fly, center: THREE.Vector3): void {
    const a = this.rng() * Math.PI * 2;
    const r = 2.5 + this.rng() * 9;
    const x = center.x + Math.cos(a) * r;
    const z = center.z + Math.sin(a) * r;
    fly.target.set(x, this.terrain.heightAt(x, z) + 1.0 + this.rng() * 1.2, z);
    fly.hover = 0.6 + this.rng() * 2.4;
  }

  update(dt: number, center: THREE.Vector3, wind: THREE.Vector2, gust: number): void {
    this.time += dt;
    this.flies.forEach((fly, i) => {
      fly.hover -= dt;
      if (fly.hover <= 0 || fly.pos.distanceTo(center) > 16) this.pickTarget(fly, center);
      const toTarget = fly.target.clone().sub(fly.pos);
      const dist = toTarget.length();
      const dart = dist > 0.3 ? 9 : 2;
      fly.vel.lerp(toTarget.multiplyScalar(dart / Math.max(dist, 0.3) * clamp(dist, 0, 1.5)), 1 - Math.exp(-dt * 4));
      fly.vel.x += wind.x * gust * 0.8 * dt;
      fly.vel.z += wind.y * gust * 0.8 * dt;
      fly.pos.addScaledVector(fly.vel, dt);
      fly.pos.y += Math.sin(this.time * 3 + fly.phase) * 0.004;
      const horizontal = Math.hypot(fly.vel.x, fly.vel.z);
      if (horizontal > 0.2) fly.heading = lerp(fly.heading, Math.atan2(fly.vel.x, fly.vel.z), 1 - Math.exp(-dt * 8));
      this.e.set(0, fly.heading, 0);
      this.q.setFromEuler(this.e);
      this.m.compose(fly.pos, this.q, this.s);
      this.bodies.setMatrixAt(i, this.m);
      const flap = Math.sin(this.time * 70 + fly.phase * 10) * 0.55;
      for (let w = 0; w < 4; w++) {
        const side = w < 2 ? 1 : -1;
        const front = w % 2 === 0 ? 0.012 : -0.006;
        this.e.set(0, fly.heading + (side > 0 ? 0 : Math.PI), (side > 0 ? 1 : 1) * (flap + (w % 2 === 0 ? 0 : 0.4)));
        this.q.setFromEuler(this.e);
        const offset = new THREE.Vector3(0, 0.004, front).applyAxisAngle(new THREE.Vector3(0, 1, 0), fly.heading);
        this.m.compose(fly.pos.clone().add(offset), this.q, this.s);
        this.wings.setMatrixAt(i * 4 + w, this.m);
      }
    });
    this.bodies.instanceMatrix.needsUpdate = true;
    this.wings.instanceMatrix.needsUpdate = true;
  }
}

const BIRD_COUNT = 14;

/** Distant flock drifting across the sky now and then. */
export class Birds {
  readonly mesh: THREE.InstancedMesh;
  private readonly rng = mulberry32(31);
  private readonly offsets: THREE.Vector3[] = [];
  private readonly phases: number[] = [];
  private readonly origin = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private progress = 1;
  private wait = 12;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly v = new THREE.Vector3();
  private time = 0;

  constructor(env: Environment) {
    const geo = new THREE.BufferGeometry();
    // Simple gliding silhouette: two swept wings and a body.
    const verts = new Float32Array([
      0, 0, 0.18, -0.9, 0.05, -0.1, 0, 0, -0.1,
      0, 0, 0.18, 0, 0, -0.1, 0.9, 0.05, -0.1,
      0, 0, 0.3, -0.06, 0, -0.35, 0.06, 0, -0.35,
    ]);
    geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    geo.computeVertexNormals();
    const mat = new THREE.ShaderMaterial({
      uniforms: { ...env.uniforms },
      side: THREE.DoubleSide,
      vertexShader: /* glsl */ `
        uniform float uTime;
        varying float vFade;
        void main() {
          vec3 p = position;
          float phase = instanceMatrix[3].x * 0.37 + instanceMatrix[3].z * 0.21;
          p.y += abs(p.x) * sin(uTime * 7.0 + phase) * 0.55;
          vec4 wp = modelMatrix * instanceMatrix * vec4(p, 1.0);
          vFade = 1.0;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uSkyHorizon;
        void main() { gl_FragColor = vec4(uSkyHorizon * 0.22, 1.0); }
      `,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, BIRD_COUNT);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    for (let i = 0; i < BIRD_COUNT; i++) {
      this.offsets.push(new THREE.Vector3((this.rng() - 0.5) * 30, (this.rng() - 0.5) * 8, (this.rng() - 0.5) * 30));
      this.phases.push(this.rng() * 10);
    }
  }

  update(dt: number, center: THREE.Vector3): void {
    this.time += dt;
    if (this.progress >= 1) {
      this.wait -= dt;
      this.mesh.visible = false;
      if (this.wait > 0) return;
      const a = this.rng() * Math.PI * 2;
      const r = 220 + this.rng() * 160;
      this.origin.set(center.x + Math.cos(a) * r, center.y + 55 + this.rng() * 50, center.z + Math.sin(a) * r);
      const b = a + Math.PI + (this.rng() - 0.5) * 1.2;
      this.dir.set(center.x + Math.cos(b) * r - this.origin.x, 0, center.z + Math.sin(b) * r - this.origin.z);
      this.progress = 0;
      this.wait = 25 + this.rng() * 35;
    }
    const length = this.dir.length();
    this.progress += (dt * 9) / length;
    this.mesh.visible = true;
    const heading = Math.atan2(this.dir.x, this.dir.z);
    this.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), heading);
    for (let i = 0; i < BIRD_COUNT; i++) {
      this.v.copy(this.origin).addScaledVector(this.dir, this.progress);
      const o = this.offsets[i];
      this.v.x += o.x + Math.sin(this.time * 0.3 + this.phases[i]) * 2;
      this.v.y += o.y + Math.sin(this.time * 0.5 + this.phases[i]) * 1.5;
      this.v.z += o.z;
      this.m.compose(this.v, this.q, new THREE.Vector3(0.9, 0.9, 0.9));
      this.mesh.setMatrixAt(i, this.m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

export function dampVec2(current: THREE.Vector2, target: THREE.Vector2, lambda: number, dt: number): void {
  current.x = damp(current.x, target.x, lambda, dt);
  current.y = damp(current.y, target.y, lambda, dt);
}
